import React from 'react';
import dynamic from 'next/dynamic';
import type {
	GetStaticProps,
	GetStaticPropsContext,
	GetStaticPropsResult,
} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';

import {Box, Flex, LoadingSpinner, Text, VStack} from '@tempo/core';

import Layout from '@components/Layout';
import {useURLParams} from '@providers/URLParamsProvider';
import {ReceiverGameProvider} from '@providers/ReceiverGameProvider';
import Unlogged from '@components/Unlogged';
import Header from '@components/Header';
import ScreenRotate from '@components/ScreenRotate';

import {
	useCampaignQuery,
	usePersonalInfoQuery,
	usePublicCampaignQuery,
} from 'src/graphql/generated';
import PreviewProvider from '@providers/PreviewProvider';
import {AUTH_STATUS, useAuth} from '@providers/AuthProvider';
import StreamLogProvider from '@providers/StreamLogProvider';
import {jwtDecode} from 'jwt-decode';

const ErrorScreen = dynamic(() => import('@components/ErrorScreen'), {
	ssr: false,
});

const Story = dynamic(() => import('@components/Story'), {
	ssr: false,
});

function IndexPage() {
	const {campaign} = useURLParams();
	const [{authStatus}] = useAuth();

	const isLeaderBoard = campaign.includes('mdy-quiz-leaderboard');
	const isPublicCampaign = campaign.startsWith('public-');
	const isAuthLoading = authStatus === AUTH_STATUS.LOADING;
	const isLoggedAsUser = authStatus === AUTH_STATUS.LOGGED_AS_USER;

	const shouldDisplayUnloggedError =
		!isAuthLoading && !isPublicCampaign && !isLoggedAsUser;

	const {
		loading: campaignLoading,
		error: campaignError,
		data: campaignData,
	} = useCampaignQuery({
		variables: {
			campaign: campaign === 'dzr-debug' ? 'mdy' : campaign,
		},
		skip: isAuthLoading || isPublicCampaign || !isLoggedAsUser,
	});

	const {
		loading: publicCampaignLoading,
		error: publicCampaignError,
		data: publicCampaignData,
	} = usePublicCampaignQuery({
		variables: {
			token: campaign.replace('public-', ''),
		},
		skip: isAuthLoading || !isPublicCampaign,
	});

	const {data: personalInfoData} = usePersonalInfoQuery({
		skip: isAuthLoading,
	});

	if (isAuthLoading || campaignLoading || publicCampaignLoading) {
		return (
			<Layout>
				<Flex h="full" w="full" justifyContent="center" alignItems="center">
					<LoadingSpinner />
				</Flex>
			</Layout>
		);
	}

	if (campaignError || publicCampaignError) {
		return (
			<Layout>
				<ErrorScreen
					errorMessage={campaignError?.message || 'An error occurred'}
				/>
			</Layout>
		);
	}

	if (campaign === 'dzr-debug') {
		return (
			<Layout>
				<Box pos="relative" h="full" w="full" backgroundColor="#000">
					<Header color="#FFF" cardId="debug" />
					<VStack>
						<Text>User id: {personalInfoData?.me?.id}</Text>
						<Text>Username: {personalInfoData?.me?.user.name}</Text>
						<Text>
							Campaign name: {campaignData?.me?.personalStats?.campaignTitle}
						</Text>
						<Text>
							Cards count: {campaignData?.me?.personalStats?.cards.length}
						</Text>
						<Text>UA: {navigator.userAgent}</Text>
					</VStack>
				</Box>
			</Layout>
		);
	}

	if (shouldDisplayUnloggedError) {
		return (
			<Layout>
				<Unlogged />
			</Layout>
		);
	}

	const streamContext = {
		type: 'MyDeezerYear',
		id: personalInfoData?.me?.id || '',
	};

	if (isPublicCampaign) {
		const publicCampaignToken = campaign.replace('public-', '');

		const decoded = jwtDecode<{
			ownerId: string;
			campaignName: string;
			saveScoreToken: string;
		}>(publicCampaignToken);

		if (decoded.campaignName) {
			streamContext.type = decoded.campaignName;
		}

		if (decoded.ownerId) {
			streamContext.id = decoded.ownerId;
		}
	}

	return (
		<Layout>
			<Box pos="relative" h="full" w="full" backgroundColor="#000">
				{isPublicCampaign ? (
					<ReceiverGameProvider>
						<PreviewProvider>
							<StreamLogProvider streamContext={streamContext}>
								<Story
									isPublicCampaign={isPublicCampaign}
									storyQueryResult={publicCampaignData?.deezerStory || null}
								/>
							</StreamLogProvider>
						</PreviewProvider>
					</ReceiverGameProvider>
				) : (
					<PreviewProvider>
						<StreamLogProvider streamContext={streamContext}>
							<Story
								isPublicCampaign={isPublicCampaign || isLeaderBoard}
								storyQueryResult={campaignData?.me?.personalStats || null}
							/>
						</StreamLogProvider>
					</PreviewProvider>
				)}
			</Box>
			<ScreenRotate />
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({
	locale,
	locales,
}): Promise<GetStaticPropsResult<GetStaticPropsContext>> => {
	return {
		props: {
			locale,
			locales,
			...(await serverSideTranslations(locale || '', ['common'])),
		},
	};
};

export default IndexPage;
